import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { AuthClaims } from '../models/auth-claims';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly msalService = inject(MsalService);

  isAuthenticated(): boolean {
    return !!this.getActiveAccount();
  }

  getActiveAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const [firstAccount] = this.msalService.instance.getAllAccounts();

    if (firstAccount) {
      this.msalService.instance.setActiveAccount(firstAccount);
      return firstAccount;
    }

    return null;
  }

  login(returnUrl?: string): void {
    const redirectStartPage = returnUrl
      ? new URL(returnUrl, window.location.origin).href
      : window.location.href;

    this.msalService.loginRedirect({
      redirectStartPage,
      scopes: [environment.auth.apiScope],
    });
  }

  logout(): void {
    this.msalService.logoutRedirect();
  }

  getRequiredRoles(route: ActivatedRouteSnapshot): string[] {
    let roles: string[] = [];

    if (route.data?.['roles']) {
      roles = [...roles, ...route.data['roles']];
    }

    let parent = route.parent;
    while (parent) {
      if (parent.data?.['roles']) {
        roles = [...roles, ...parent.data['roles']];
      }
      parent = parent.parent;
    }

    return [...new Set(roles)];
  }

  getRoles(): string[] {
    const account = this.getActiveAccount();
    const claims = account?.idTokenClaims as AuthClaims | undefined;
    return claims?.roles ?? [];
  }

  hasRequiredRoles(roles: string[]): boolean {
    if (!roles.length) {
      return true;
    }

    const userRoles = this.getRoles();
    return roles.some((role) => userRoles.includes(role));
  }

  checkMenuAccess(roles: string[] = []): boolean {
    return this.hasRequiredRoles(roles);
  }
}
